// Appointment date changes on assessment modules -> canonical analytics event.
//
// Bridged from the single audit appender in appointmentDateHelpers.js, which
// every change already passes through: the appointment-management dialog's
// set / change / remove, and the automatic set when completing a module infers
// its own appointment date. One writer, one mirror, no call-site edits.
//
// The audit entry carries the acting clinician's display name and the actual
// dates. The name never travels; the dates travel only as a DELTA in days -
// how far the appointment moved - because "appointments slip by a week on
// average" is the insight, and the calendar date itself is a real-world
// meeting with an identifiable person.

import { emitAnalyticsEvent } from './emitEvent';

// The audit trail's action vocabulary, mapped onto the registry's enum. The
// registry field is closed and a stray value invalidates the whole event, so
// an unmapped action emits nothing rather than guessing - and the audit trail
// still holds it, so nothing is lost.
const ACTION_BY_AUDIT_ACTION = {
  auto_set: 'set',
  set: 'set',
  updated: 'changed',
  removed: 'cleared',
};

const TRIGGERS = ['moduleCompletion', 'manual'];

/** Whole days between two YYYY-MM-DD calendar strings, or undefined. */
function deltaDays(previousDate, newDate) {
  if (typeof previousDate !== 'string' || typeof newDate !== 'string') return undefined;
  const before = Date.parse(`${previousDate}T00:00:00Z`);
  const after = Date.parse(`${newDate}T00:00:00Z`);
  if (Number.isNaN(before) || Number.isNaN(after)) return undefined;
  return Math.round((after - before) / 86400000);
}

/**
 * Mirror one appointment date change into the analytics event stream.
 * Fire-and-forget; never throws into the audit appender.
 */
export function emitAppointmentDateEvent({
  patientId, module, action, trigger, previousDate, newDate,
} = {}) {
  try {
    if (!patientId || !module) return false;
    const mappedAction = ACTION_BY_AUDIT_ACTION[action];
    if (!mappedAction) return false;

    const payload = {
      patient_id: String(patientId),
      module: String(module),
      action: mappedAction,
      // The audit trail omits trigger on older entries; the registry requires
      // it, and a change reaching this appender outside the auto-set path is a
      // manual one by construction.
      trigger: TRIGGERS.includes(trigger) ? trigger : 'manual',
    };
    const delta = deltaDays(previousDate, newDate);
    if (delta !== undefined) payload.date_delta_days = delta;

    return emitAnalyticsEvent('assessment_appointment_date_set', payload);
  } catch (error) {
    return false;
  }
}


// The registry's module_type vocabulary for the completion event, from the
// module keys the appointment helper already validates (VALID_MODULE_KEYS in
// appointmentDateHelpers.js). ADHD uses ci/ssi/dd/pad. Adult Autism shares
// dd/pad and adds two of its own: `ddi` (the Diagnostic Developmental
// Interview - History + SSI completed as one appointment) and `obs`
// (Behavioural Observation). The registry types module_type for this event as a
// free-form string (analytics/events.schema.json), so new values need no schema
// change; the enum on assessment_module_opened is a separate event.
const MODULE_TYPE_BY_KEY = {
  ci: 'clinicalInterview',
  ssi: 'semiStructuredInterview',
  dd: 'diagnosisDecision',
  pad: 'postAssessmentDiscussion',
  ddi: 'diagnosticDevelopmentalInterview',
  obs: 'behaviouralObservation',
};

/**
 * A module's FIRST completion - the write-once stamp was written in the same
 * transaction the caller just committed. Covers every module that passes
 * through the appointment helper: the four ADHD modules and the Adult Autism
 * stages (which complete via the same helper since gaining firstCompletedAt).
 */
export function emitModuleCompleted({ patientId, moduleKey } = {}) {
  try {
    const moduleType = MODULE_TYPE_BY_KEY[moduleKey];
    if (!patientId || !moduleType) return false;
    return emitAnalyticsEvent('assessment_module_completed', {
      patient_id: String(patientId),
      module_type: moduleType,
    });
  } catch (error) {
    return false;
  }
}
